import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AGE_OPTIONS = [
  { value: '', label: 'Selecteer leeftijd' },
  { value: '0-2 jaar', label: '0-2 jaar' },
  { value: '3 jaar', label: '3 jaar' },
  { value: '4 jaar', label: '4 jaar' },
  { value: '5 jaar', label: '5 jaar' },
  { value: '6 jaar', label: '6 jaar' },
  { value: '7 jaar', label: '7 jaar' },
  { value: '8 jaar', label: '8 jaar' },
  { value: '9 jaar', label: '9 jaar' },
  { value: '10 jaar', label: '10 jaar' },
  { value: '11 jaar', label: '11 jaar' },
  { value: '12 jaar', label: '12 jaar' },
  { value: '13 jaar', label: '13 jaar' },
  { value: '14 jaar', label: '14 jaar' },
  { value: '15 jaar', label: '15 jaar' },
  { value: '16 jaar', label: '16 jaar' },
  { value: '17 jaar', label: '17 jaar' },
];

const NieuwjaarsreceptieTab = () => {
  const [registrations, setRegistrations] = useState([]);
  const [stats, setStats] = useState(null);
  const [showWalkInForm, setShowWalkInForm] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingRegistration, setEditingRegistration] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [walkInForm, setWalkInForm] = useState({
    first_name: '',
    last_name: '',
    street_address: '',
    postal_code: '',
    city: '',
    num_adults: 2,
    num_children: 0,
    children_ages: [],
    dietary_requirements: '',
  });
  const [editForm, setEditForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    street_address: '',
    postal_code: '',
    city: '',
    num_adults: 2,
    num_children: 0,
    children_ages: [],
    dietary_requirements: '',
    comments: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [regsRes, statsRes] = await Promise.all([
        axios.get(`${API}/nieuwjaarsreceptie/registrations`),
        axios.get(`${API}/nieuwjaarsreceptie/stats`),
      ]);
      setRegistrations(regsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Fout bij ophalen gegevens');
    }
  };

  const handleWalkInSubmit = async (e) => {
    e.preventDefault();
    
    if (walkInForm.num_children > 0 && walkInForm.children_ages.some(age => !age)) {
      toast.error('Vul alle leeftijden van de kinderen in');
      return;
    }

    try {
      await axios.post(`${API}/nieuwjaarsreceptie/register`, {
        ...walkInForm,
        email: `walk-in-${Date.now()}@feestcomite.local`,
        registration_type: 'walk_in',
        privacy_agreed: true,
      });
      
      toast.success('Walk-in registratie toegevoegd en ingecheckt!');
      setShowWalkInForm(false);
      setWalkInForm({
        first_name: '',
        last_name: '',
        street_address: '',
        postal_code: '',
        city: '',
        num_adults: 2,
        num_children: 0,
        children_ages: [],
        dietary_requirements: '',
      });
      fetchData();
    } catch (error) {
      console.error('Walk-in error:', error);
      toast.error('Fout bij toevoegen walk-in');
    }
  };

  const toggleAttendance = async (id, currentStatus) => {
    try {
      await axios.patch(`${API}/nieuwjaarsreceptie/registrations/${id}/attendance`, {
        is_present: !currentStatus
      });
      toast.success(currentStatus ? 'Uitchecken succesvol' : 'Inchecken succesvol');
      fetchData();
    } catch (error) {
      console.error('Attendance error:', error);
      toast.error('Fout bij updaten aanwezigheid');
    }
  };

  const deleteRegistration = async (id) => {
    if (!window.confirm('Weet je zeker dat je deze inschrijving wilt verwijderen?')) {
      return;
    }

    try {
      await axios.delete(`${API}/nieuwjaarsreceptie/registrations/${id}`);
      toast.success('Inschrijving verwijderd');
      fetchData();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Fout bij verwijderen');
    }
  };

  const openEditDialog = (registration) => {
    setEditingRegistration(registration);
    setEditForm({
      first_name: registration.first_name,
      last_name: registration.last_name,
      email: registration.email,
      phone: registration.phone || '',
      street_address: registration.street_address || '',
      postal_code: registration.postal_code || '',
      city: registration.city || '',
      num_adults: registration.num_adults,
      num_children: registration.num_children,
      children_ages: registration.children_ages || [],
      dietary_requirements: registration.dietary_requirements || '',
      comments: registration.comments || '',
    });
    setShowEditDialog(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    if (editForm.num_children > 0 && editForm.children_ages.some(age => !age)) {
      toast.error('Vul alle leeftijden van de kinderen in');
      return;
    }

    try {
      await axios.patch(`${API}/nieuwjaarsreceptie/registrations/${editingRegistration.id}`, {
        ...editForm,
        privacy_agreed: true,
        registration_type: editingRegistration.registration_type
      });
      
      toast.success('Registratie succesvol bijgewerkt!');
      setShowEditDialog(false);
      setEditingRegistration(null);
      fetchData();
    } catch (error) {
      console.error('Edit error:', error);
      toast.error('Fout bij bijwerken registratie');
    }
  };

  const exportToCSV = async () => {
    try {
      const response = await axios.get(`${API}/nieuwjaarsreceptie/export`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `nieuwjaarsreceptie_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('CSV geëxporteerd!');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Fout bij exporteren');
    }
  };

  const filteredRegistrations = registrations.filter(reg => {
    const matchesSearch = 
      reg.first_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      reg.last_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      reg.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (reg.street_address && reg.street_address.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (reg.postal_code && reg.postal_code.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (reg.city && reg.city.toLowerCase().includes(searchQuery.toLowerCase()));
    
    if (filterType === 'present') return matchesSearch && reg.is_present;
    if (filterType === 'not_present') return matchesSearch && !reg.is_present;
    if (filterType === 'with_children') return matchesSearch && reg.num_children > 0;
    if (filterType === 'walk_in') return matchesSearch && reg.registration_type === 'walk_in';
    if (filterType === 'online') return matchesSearch && reg.registration_type === 'online';
    
    return matchesSearch;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Nieuwjaarsreceptie 2026</h2>
            <p className="text-gray-600">Zondag 18 januari 2026 om 10:30</p>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={() => setShowWalkInForm(true)}
              className="bg-green-600 hover:bg-green-700"
            >
              + Walk-in Registratie
            </Button>
            <Button
              onClick={exportToCSV}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Exporteer naar Excel
            </Button>
          </div>
        </div>
      </div>

      {/* Statistics */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-lg p-6 text-white">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm opacity-90">Totaal Inschrijvingen</span>
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <div className="text-4xl font-bold">{stats.total_registrations}</div>
            <p className="text-sm opacity-75 mt-1">families/personen</p>
          </div>

          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow-lg p-6 text-white">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm opacity-90">Totaal Gasten</span>
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </div>
            <div className="text-4xl font-bold">{stats.total_guests}</div>
            <p className="text-sm opacity-75 mt-1">personen totaal</p>
          </div>

          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shadow-lg p-6 text-white">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm opacity-90">Aanwezig</span>
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="text-4xl font-bold">{stats.total_guests_present}</div>
            <p className="text-sm opacity-75 mt-1">{stats.attendance_percentage}% ingecheckt</p>
          </div>

          <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg shadow-lg p-6 text-white">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm opacity-90">Kinderen</span>
              <span className="text-2xl">👶</span>
            </div>
            <div className="text-4xl font-bold">{stats.total_children}</div>
            <p className="text-sm opacity-75 mt-1">
              {stats.total_guests > 0 ? Math.round((stats.total_children / stats.total_guests) * 100) : 0}% van gasten
            </p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4">
        <div className="flex gap-4 items-center flex-wrap">
          <Input
            type="text"
            placeholder="🔍 Zoek op naam, email, straat, postcode, gemeente..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1 min-w-[200px]"
          />
          
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600"
          >
            <option value="all">Alle inschrijvingen</option>
            <option value="present">Alleen aanwezig ✓</option>
            <option value="not_present">Nog niet aanwezig</option>
            <option value="with_children">Met kinderen</option>
            <option value="online">Online registraties</option>
            <option value="walk_in">Walk-in registraties</option>
          </select>
        </div>
      </div>

      {/* Registrations Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b-2 border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Aanwezig</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Naam</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Straat</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Postcode</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Gemeente</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Volw.</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Kind.</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Totaal</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dieet</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Opmerkingen</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Acties</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredRegistrations.length === 0 ? (
                <tr>
                  <td colSpan="13" className="px-6 py-8 text-center text-gray-500">
                    Geen inschrijvingen gevonden
                  </td>
                </tr>
              ) : (
                filteredRegistrations.map((reg) => (
                  <tr 
                    key={reg.id} 
                    className={`hover:bg-gray-50 ${reg.is_present ? 'bg-green-50' : ''}`}
                  >
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={reg.is_present}
                        onChange={() => toggleAttendance(reg.id, reg.is_present)}
                        className="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500 cursor-pointer"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <div className="text-sm font-bold text-gray-900">
                          {reg.first_name} {reg.last_name}
                        </div>
                        <div className="text-xs text-gray-500">
                          {new Date(reg.created_at).toLocaleDateString('nl-BE')}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm text-gray-900">{reg.email}</div>
                      {reg.phone && <div className="text-xs text-gray-500">{reg.phone}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm text-gray-900">{reg.street_address || '-'}</div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="text-sm text-gray-900">{reg.postal_code || '-'}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm text-gray-900">{reg.city || '-'}</div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="text-lg font-bold text-gray-900">{reg.num_adults}</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="text-lg font-bold text-orange-600">{reg.num_children}</span>
                      {reg.num_children > 0 && (
                        <div className="text-xs text-gray-500">{reg.children_ages.join(', ')}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-800">
                        {reg.total_persons}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-gray-600">{reg.dietary_requirements || '-'}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-gray-600" title={reg.comments}>
                        {reg.comments ? (reg.comments.length > 20 ? reg.comments.substring(0, 20) + '...' : reg.comments) : '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${
                        reg.registration_type === 'walk_in' 
                          ? 'bg-orange-100 text-orange-800' 
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {reg.registration_type === 'walk_in' ? 'Walk-in' : 'Online'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2 justify-center">
                        <button
                          onClick={() => openEditDialog(reg)}
                          className="text-blue-600 hover:text-blue-900 font-medium text-xs"
                        >
                          Bewerken
                        </button>
                        <button
                          onClick={() => deleteRegistration(reg.id)}
                          className="text-red-600 hover:text-red-900 font-medium text-xs"
                        >
                          Verwijder
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Registration Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Registratie Bewerken</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleEditSubmit} className="space-y-6">
            {/* Contactgegevens */}
            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="font-bold text-gray-900 mb-3">Contactgegevens</h3>
              <div className="grid grid-cols-2 gap-4 mb-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Voornaam *</label>
                  <Input
                    type="text"
                    value={editForm.first_name}
                    onChange={(e) => setEditForm({...editForm, first_name: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Achternaam *</label>
                  <Input
                    type="text"
                    value={editForm.last_name}
                    onChange={(e) => setEditForm({...editForm, last_name: e.target.value})}
                    required
                  />
                </div>
              </div>
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                <Input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Telefoon</label>
                <Input
                  type="tel"
                  value={editForm.phone}
                  onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                  placeholder="0478 12 34 56"
                />
              </div>
            </div>

            {/* Adresgegevens */}
            <div className="bg-green-50 p-4 rounded-lg">
              <h3 className="font-bold text-gray-900 mb-3">Adresgegevens</h3>
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-2">Straat + Huisnummer *</label>
                <Input
                  type="text"
                  value={editForm.street_address}
                  onChange={(e) => setEditForm({...editForm, street_address: e.target.value})}
                  required
                  placeholder="Leupegem 123"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Postcode *</label>
                  <Input
                    type="text"
                    value={editForm.postal_code}
                    onChange={(e) => setEditForm({...editForm, postal_code: e.target.value})}
                    required
                    placeholder="9700"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Gemeente *</label>
                  <Input
                    type="text"
                    value={editForm.city}
                    onChange={(e) => setEditForm({...editForm, city: e.target.value})}
                    required
                    placeholder="Oudenaarde"
                  />
                </div>
              </div>
            </div>

            {/* Aantal personen */}
            <div className="bg-purple-50 p-4 rounded-lg">
              <h3 className="font-bold text-gray-900 mb-3">Aantal personen</h3>
              <div className="grid grid-cols-2 gap-4 mb-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Aantal volwassenen *</label>
                  <select
                    value={editForm.num_adults}
                    onChange={(e) => setEditForm({...editForm, num_adults: parseInt(e.target.value)})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    required
                  >
                    {[1,2,3,4,5,6].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Aantal kinderen</label>
                  <select
                    value={editForm.num_children}
                    onChange={(e) => {
                      const numChildren = parseInt(e.target.value);
                      setEditForm({
                        ...editForm,
                        num_children: numChildren,
                        children_ages: Array(numChildren).fill('')
                      });
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  >
                    {[0,1,2,3,4,5].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
              </div>

              {editForm.num_children > 0 && (
                <div className="bg-white p-3 rounded-lg">
                  <p className="text-sm font-medium text-gray-700 mb-2">Leeftijden kinderen *</p>
                  <div className="grid grid-cols-2 gap-2">
                    {Array.from({ length: editForm.num_children }, (_, i) => (
                      <select
                        key={i}
                        value={editForm.children_ages[i] || ''}
                        onChange={(e) => {
                          const newAges = [...editForm.children_ages];
                          newAges[i] = e.target.value;
                          setEditForm({...editForm, children_ages: newAges});
                        }}
                        required
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      >
                        {AGE_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Overige informatie */}
            <div className="bg-orange-50 p-4 rounded-lg">
              <h3 className="font-bold text-gray-900 mb-3">Overige informatie</h3>
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-2">Dieetwensen</label>
                <Textarea
                  value={editForm.dietary_requirements}
                  onChange={(e) => setEditForm({...editForm, dietary_requirements: e.target.value})}
                  rows={2}
                  placeholder="Vegetarisch, veganistisch, allergieën..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Opmerkingen</label>
                <Textarea
                  value={editForm.comments}
                  onChange={(e) => setEditForm({...editForm, comments: e.target.value})}
                  rows={3}
                  placeholder="Aanvullende opmerkingen..."
                />
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700">
                Wijzigingen Opslaan
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowEditDialog(false)}
                className="flex-1"
              >
                Annuleren
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Walk-in Form Dialog */}
      <Dialog open={showWalkInForm} onOpenChange={setShowWalkInForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Walk-in Registratie</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleWalkInSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Voornaam *</label>
                <Input
                  type="text"
                  value={walkInForm.first_name}
                  onChange={(e) => setWalkInForm({...walkInForm, first_name: e.target.value})}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Achternaam *</label>
                <Input
                  type="text"
                  value={walkInForm.last_name}
                  onChange={(e) => setWalkInForm({...walkInForm, last_name: e.target.value})}
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Straat + Huisnummer *</label>
              <Input
                type="text"
                value={walkInForm.street_address}
                onChange={(e) => setWalkInForm({...walkInForm, street_address: e.target.value})}
                required
                placeholder="Leupegem 123"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Postcode *</label>
                <Input
                  type="text"
                  value={walkInForm.postal_code}
                  onChange={(e) => setWalkInForm({...walkInForm, postal_code: e.target.value})}
                  required
                  placeholder="9700"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Gemeente *</label>
                <Input
                  type="text"
                  value={walkInForm.city}
                  onChange={(e) => setWalkInForm({...walkInForm, city: e.target.value})}
                  required
                  placeholder="Oudenaarde"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Aantal volwassenen *</label>
                <select
                  value={walkInForm.num_adults}
                  onChange={(e) => setWalkInForm({...walkInForm, num_adults: parseInt(e.target.value)})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  required
                >
                  {[1,2,3,4,5,6].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Aantal kinderen</label>
                <select
                  value={walkInForm.num_children}
                  onChange={(e) => {
                    const numChildren = parseInt(e.target.value);
                    setWalkInForm({
                      ...walkInForm,
                      num_children: numChildren,
                      children_ages: Array(numChildren).fill('')
                    });
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                >
                  {[0,1,2,3,4,5].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
            </div>

            {walkInForm.num_children > 0 && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm font-medium text-gray-700 mb-3">Leeftijden kinderen *</p>
                <div className="space-y-2">
                  {Array.from({ length: walkInForm.num_children }, (_, i) => (
                    <select
                      key={i}
                      value={walkInForm.children_ages[i] || ''}
                      onChange={(e) => {
                        const newAges = [...walkInForm.children_ages];
                        newAges[i] = e.target.value;
                        setWalkInForm({...walkInForm, children_ages: newAges});
                      }}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      {AGE_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  ))}
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Dieetwensen (optioneel)</label>
              <Textarea
                value={walkInForm.dietary_requirements}
                onChange={(e) => setWalkInForm({...walkInForm, dietary_requirements: e.target.value})}
                rows={2}
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="submit" className="flex-1 bg-green-600 hover:bg-green-700">
                Toevoegen & Check-in
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowWalkInForm(false)}
                className="flex-1"
              >
                Annuleren
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NieuwjaarsreceptieTab;
